import React, { useMemo } from 'react';
import { TranslationKey } from './../../../../../../types/translation/TranslationKey';
import { ProductMasterData } from '../../../../../../types/product_master_data/productMasterData';
import { t, translate } from '../../../../../../types/translation/Translator';
import { MetricUnit, UnitOfMeasureUtil, UnitSystem, UnitType } from '../../../../../../types/uom';
import { countries } from '../../../../../../util/country';
import { toMap } from '../../../../../../util/map.util';
import { Checkbox } from '../../../../../components/Checkbox';
import { DropdownSearchSelect } from '../../../../../components/Dropdown';
import { TextInput } from '../../../../../components/TextInput';
import { Feedback } from '../../../../../components/Feedback/feedback';

interface OnboardingReferenceDataFormProps {
  existing: Map<string, ProductMasterData>;
  productMasterData: ProductMasterData;
  setProductMasterData: (productMasterData: ProductMasterData) => void;
}

export default function OnboardingReferenceDataForm({
  existing,
  productMasterData,
  setProductMasterData,
}: OnboardingReferenceDataFormProps) {
  const existingByNumber = useMemo(() => {
    return toMap([...existing.values()], 'productNumber');
  }, [existing]);

  const getAvailableUnits = () => {
    if (!productMasterData.unitOfMeasure?.system || !productMasterData.unitOfMeasure?.unitType) {
      return [];
    }

    const system = productMasterData.unitOfMeasure.system;
    const unitType = productMasterData.unitOfMeasure.unitType;

    if (system === UnitSystem.metric) {
      return Object.values(MetricUnit);
    }

    return [];
  };

  return (
    <div className='grid gap-4'>
      <div className='grid grid-cols-2 gap-2'>
        <TextInput
          mandatory
          label={t(TranslationKey.productName).singular.label}
          onChange={value => setProductMasterData(productMasterData.withProductName(value))}
          placeholder={'Your product name'}
          value={productMasterData.productName}
        />
        <TextInput
          mandatory
          label={t(TranslationKey.productNumber).singular.label}
          onChange={value => setProductMasterData(productMasterData.withProductNumber(value))}
          placeholder={'Ex: PRD-001, SKU-12345'}
          value={productMasterData.productNumber}
          feedback={value => {
            if (!value) return Feedback.mandatory(value);
            if (existingByNumber.has(value) && existingByNumber.get(value)?.id !== productMasterData.id) {
              return { type: Feedback.Type.error, text: 'Product with this number already exists!' };
            }
          }}
        />
      </div>

      <div className='grid grid-cols-3 gap-2'>
        <Checkbox
          label={t(TranslationKey.serialManaged).singular.label}
          value={productMasterData.serialManaged || false}
          onChange={value => setProductMasterData(productMasterData.withSerialManaged(value))}
        />
        <Checkbox
          label={t(TranslationKey.lotManaged).singular.label}
          value={productMasterData.lotManaged || false}
          onChange={value => setProductMasterData(productMasterData.withLotManaged(value))}
        />
        <Checkbox
          label={t(TranslationKey.lpnManaged).singular.label}
          value={productMasterData.lpnManaged || false}
          onChange={value => setProductMasterData(productMasterData.withLpnManaged(value))}
        />
      </div>

      <div className='grid grid-cols-2 gap-2'>
        <DropdownSearchSelect
          onSelect={value =>
            setProductMasterData(productMasterData.withCountryOfOrigin(value ? value.cca2 : undefined))
          }
          placeholder={'Select a country'}
          label={t(TranslationKey.countryOfOrigin).singular.label}
          value={countries.find(c => c.cca2 === productMasterData.countryOfOrigin)}
          items={countries}
          textBuilder={country => country.name.common}
        />
        <TextInput
          label={t(TranslationKey.globalTradeItemNumber).singular.label.toUpperCase()}
          placeholder={'EAN'}
          onChange={value => setProductMasterData(productMasterData.withEAN(value))}
          value={productMasterData.globalTradeItemNumber}
        />
      </div>

      <div className='grid grid-cols-3 gap-2'>
        <DropdownSearchSelect
          mandatory
          onSelect={value => {
            setProductMasterData(productMasterData.withUnitType(value));
          }}
          placeholder={'Select type'}
          label={'Type'}
          value={productMasterData.unitOfMeasure?.unitType}
          items={Object.values(UnitType)}
          textBuilder={type => UnitOfMeasureUtil.unitTypeToString(type)}
        />

        {productMasterData.unitOfMeasure?.unitType &&
          productMasterData.unitOfMeasure?.unitType !== UnitType.quantity &&
          !UnitOfMeasureUtil.needsUnit(productMasterData.unitOfMeasure?.unitType) && (
            <>
              <DropdownSearchSelect
                mandatory={!!productMasterData.unitOfMeasure}
                onSelect={value => {
                  setProductMasterData(productMasterData.withUnitSystem(value));
                }}
                placeholder={'Select system'}
                label={'System'}
                value={productMasterData.unitOfMeasure?.system}
                items={Object.values(UnitSystem)}
                textBuilder={system => translate({ key: system as TranslationKey })}
                disabled={!productMasterData.unitOfMeasure?.unitType}
              />
              <DropdownSearchSelect
                mandatory={!!productMasterData.unitOfMeasure}
                onSelect={value => {
                  setProductMasterData(productMasterData.withUnit(value));
                }}
                placeholder={'Select unit'}
                label={t(TranslationKey.unit).singular.label}
                value={productMasterData.unitOfMeasure?.unit}
                items={getAvailableUnits()}
                textBuilder={unit => unit}
                disabled={!productMasterData.unitOfMeasure?.system || !productMasterData.unitOfMeasure?.unitType}
              />
            </>
          )}
      </div>
    </div>
  );
}
